import React, { useState, useEffect } from 'react';
import { SyncOperation, HistoricalSyncOptions } from '../types';
import { apiService } from '../services/api';
import { useWebSocket } from '../hooks/useWebSocket';
import { useAuth } from '../contexts/AuthContext';
import { SyncOperationCard } from './SyncOperationCard';
import { StartSyncForm } from './StartSyncForm';
import { WebhookDashboard } from './WebhookDashboard';
import { ErrorLogs } from './ErrorLogs';

type TabType = 'sync' | 'webhooks' | 'logs';

export const SyncDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('sync');
  const [operations, setOperations] = useState<SyncOperation[]>([]);
  const [loading, setLoading] = useState(false);
  const [startSyncLoading, setStartSyncLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { user, logout } = useAuth();
  const { isConnected, lastMessage } = window.location.hostname === 'localhost' ? useWebSocket('ws://localhost:8080/ws') : useWebSocket('wss://emberbridge.aacc.net/ws');

  const handleLogout = () => {
    logout();
  };

  // Sort operations by startedAt date (newest first) and then by ID for stability
  const sortOperations = (ops: SyncOperation[]) => {
    return [...ops].sort((a, b) => {
      // First sort by startedAt date (newest first)
      const dateA = new Date(a.startedAt).getTime();
      const dateB = new Date(b.startedAt).getTime();
      if (dateB !== dateA) {
        return dateB - dateA;
      }
      // If dates are equal, sort by ID for stability
      return b.id.localeCompare(a.id);
    });
  };

  // Load sync operations
  const loadOperations = async () => {
    try {
      setLoading(true);
      const ops = await apiService.listSyncOperations();
      setOperations(sortOperations(ops));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load operations');
    } finally {
      setLoading(false);
    }
  };

  // Handle WebSocket messages
  useEffect(() => {
    if (lastMessage) {
      if (lastMessage.type === 'sync_update') {
        // Update specific operation and maintain sort order
        setOperations(prev => {
          const updated = prev.map(op =>
            op.id === lastMessage.syncId
              ? { ...op, ...lastMessage.data }
              : op
          );
          return sortOperations(updated);
        });
      } else if (lastMessage.type === 'sync_complete' || lastMessage.type === 'sync_error') {
        // Refresh all operations when sync completes
        loadOperations();
      }
    }
  }, [lastMessage]);

  // Load operations on mount
  useEffect(() => {
    loadOperations();
    // Reduced polling frequency since WebSocket provides faster updates
    const interval = setInterval(loadOperations, 10000); // Refresh every 10 seconds (backup)
    return () => clearInterval(interval);
  }, []);

  const handleStartSync = async (options: HistoricalSyncOptions, accountName?: string) => {
    try {
      setStartSyncLoading(true);
      const result = await apiService.startHistoricalSync(options, accountName);
      console.log('Started sync:', result.syncId);
      // Operations will be updated via WebSocket
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start sync');
    } finally {
      setStartSyncLoading(false);
    }
  };

  const handleCancelSync = async (syncId: string) => {
    try {
      await apiService.cancelSync(syncId);
      await loadOperations(); // Refresh operations
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel sync');
    }
  };

  return (
    <div className="sync-dashboard">
      <header className="dashboard-header">
        <div className="header-branding">
          <img
            src="/AACC-Banner.png"
            alt="AACC Banner"
            className="aacc-banner"
          />
          <div className="header-title">
            <h1>Emberbridge Sync Portal</h1>
            <p className="header-subtitle">Real-time data synchronization between Brushfire and HubSpot</p>
          </div>
        </div>
        <div className="header-controls">
          <div className="connection-status">
            <span className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}>
              {isConnected ? '🟢 Real-time Updates' : '🔴 Polling Mode'}
            </span>
            <small className="connection-detail">
              {isConnected ? 'WebSocket connected' : 'WebSocket disconnected - using 10s polling'}
            </small>
          </div>
          <div className="user-controls">
            <span className="user-info">
              👤 {user?.name || user?.email}
            </span>
            <button onClick={handleLogout} className="logout-btn">
              🚪 Logout
            </button>
          </div>
        </div>
      </header>

      {error && (
        <div className="error-banner">
          <span>❌ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="dashboard-content">
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'sync' ? 'active' : ''}`}
            onClick={() => setActiveTab('sync')}
          >
            🔄 Sync Operations
          </button>
          <button
            className={`tab-button ${activeTab === 'webhooks' ? 'active' : ''}`}
            onClick={() => setActiveTab('webhooks')}
          >
            🔗 Webhooks
          </button>
          <button
            className={`tab-button ${activeTab === 'logs' ? 'active' : ''}`}
            onClick={() => setActiveTab('logs')}
          >
            📋 Error Logs
          </button>
        </div>

        {activeTab === 'sync' && (
          <>
            <div className="start-sync-section">
              <h2>Start New Sync</h2>
              <StartSyncForm onSubmit={handleStartSync} loading={startSyncLoading} />
            </div>

            <div className="operations-section">
              <div className="section-header">
                <h2>Sync Operations</h2>
                <button
                  onClick={loadOperations}
                  disabled={loading}
                  className="refresh-button"
                >
                  🔄 Refresh
                </button>
              </div>

              {loading && operations.length === 0 ? (
                <div className="loading">Loading operations...</div>
              ) : operations.length === 0 ? (
                <div className="no-operations">No sync operations found</div>
              ) : (
                <div className="operations-grid">
                  {operations.map(operation => (
                    <SyncOperationCard
                      key={operation.id}
                      operation={operation}
                      onCancel={handleCancelSync}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'webhooks' && (
          <WebhookDashboard />
        )}

        {activeTab === 'logs' && (
          <ErrorLogs />
        )}
      </div>
    </div>
  );
};
